import React, { createContext, useContext, useMemo } from 'react';
import { QuestionData, SubmissionFormData } from './types';
import { useDebugRole } from '@/contexts/DebugRoleContext';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';

interface SubmissionFormContextValue {
  submissionFormData: SubmissionFormData | null;
  submissionId: string | undefined;
  /** Refetch submission form data (e.g. after sub-status update) */
  refreshSubmissionForm: () => Promise<void>;
  /**
   * Every question in the form tree keyed by slug — lets fields and status
   * calculators resolve visibility transitively (through the whole
   * depends_on chain), even when an ancestor lives in another group.
   */
  questionsBySlug: ReadonlyMap<string, QuestionData>;
}

const EMPTY_QUESTIONS_BY_SLUG: ReadonlyMap<string, QuestionData> = new Map();

const SubmissionFormContext = createContext<SubmissionFormContextValue | null>(
  null
);

export const SubmissionFormProvider: React.FC<{
  children: React.ReactNode;
  submissionFormData: SubmissionFormData | null;
  submissionId: string | undefined;
  refreshSubmissionForm: () => Promise<void>;
  questionsBySlug?: ReadonlyMap<string, QuestionData>;
}> = ({
  children,
  submissionFormData,
  submissionId,
  refreshSubmissionForm,
  questionsBySlug = EMPTY_QUESTIONS_BY_SLUG,
}) => {
  const { debugRole } = useDebugRole();
  const { clarificationRoundState } = useClarificationsContext();

  // Apply debug role override to submissionFormData
  const overriddenFormData = useMemo(() => {
    if (!submissionFormData || debugRole === 'real') {
      return submissionFormData;
    }

    if (debugRole === 'customer') {
      return {
        ...submissionFormData,
        can_edit:
          clarificationRoundState === 'requested'
            ? true
            : submissionFormData.can_edit,
      };
    }

    if (debugRole === 'admin') {
      return {
        ...submissionFormData,
        can_edit: true,
      };
    }

    return submissionFormData;
  }, [submissionFormData, debugRole, clarificationRoundState]);

  return (
    <SubmissionFormContext.Provider
      value={{
        submissionFormData: overriddenFormData,
        submissionId,
        refreshSubmissionForm,
        questionsBySlug,
      }}
    >
      {children}
    </SubmissionFormContext.Provider>
  );
};

export const useSubmissionFormContext = (): SubmissionFormContextValue => {
  const context = useContext(SubmissionFormContext);
  if (!context) {
    // Return default values if context is not available (for backwards compatibility)
    return {
      submissionFormData: null,
      submissionId: undefined,
      refreshSubmissionForm: async () => {},
      questionsBySlug: EMPTY_QUESTIONS_BY_SLUG,
    };
  }
  return context;
};
